/**
 * Tablero.js — Tablero de juego SVG (26 casilleros en forma de circuito).
 * Muestra fichas de jugadores con efectos visuales según el tipo de casillero.
 *
 * Props:
 *   participantes    {Array}  - Lista de participantes del torneo
 *   posiciones       {object} - Mapa userId → casillero actual
 *   configCasilleros {object} - Mapa casillero → configuración (tipo, emoji, descripción)
 *   miUserId         {string|null} - Si se pasa, opaca las fichas de otros (usado durante animación del dado)
 */
import React from 'react';

// ── Constantes ────────────────────────────────────────────────────────────────

const TOTAL      = 26;
const COLS       = 8;
const ROWS       = 7;
const CELL_W     = 61.5;
const CELL_H     = 38;
const TABLE_W    = CELL_W * COLS;
const TABLE_H    = CELL_H * ROWS;

/** Colores de ficha por índice de participante */
const COLORES_FICHAS = [
  '#00f2ff', '#a855f7', '#f59e0b', '#22c55e',
  '#ef4444', '#ec4899', '#3b82f6', '#84cc16',
];

/** Color de borde según tipo de casillero */
const TIPO_STROKE = {
  comodin_aleatorio:   '#a855f7',   // violeta — azar
  comodin_fijo:        '#22c55e',   // verde — positivo
  bonus_avance:        '#22c55e',   // verde — positivo
  trampa_retroceso:    '#ef4444',   // rojo — negativo
  trampa_combo:        '#ef4444',   // rojo — negativo
  trampa_pierde_turno: null,        // sin color — "no avanza" (se omite)
};

// Casilleros con estilo especial fijo (esquinas del circuito)
const CASILLEROS_ESPECIALES = new Set([0]); // Solo Salida es especial fijo

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Convierte un número de casillero (0-25) a coordenadas de grilla (col, row).
 * El recorrido es en sentido horario: tope → derecha → fondo → izquierda.
 */
const gridPos = (n) => {
  if (n <= 7)  return { col: n,        row: 0 };
  if (n <= 12) return { col: COLS - 1, row: n - 7 };
  if (n <= 20) return { col: 20 - n,   row: ROWS - 1 };
  return             { col: 0,         row: 26 - n };
};

/**
 * Devuelve el estilo visual de un casillero según su configuración.
 */
const getCasilleroStyle = (n, configMap = {}) => {
  if (CASILLEROS_ESPECIALES.has(n)) {
    // Solo casillero 0 (Salida) tiene color especial. El resto son esquinas neutras.
    if (n === 0) return { stroke: '#f59e0b', fill: '#0c1420', color: '#f59e0b', label: 'Salida' };
    return { stroke: '#1e293b', fill: '#0c1420', color: '#475569', label: String(n) };
  }
  const cfg = configMap[n];
  if (cfg) {
    const strokeRaw = TIPO_STROKE[cfg.tipo];
    const stroke = strokeRaw === null ? '#1e293b' : (strokeRaw || '#a855f7');
    return { stroke, fill: '#0c1420', color: stroke === '#1e293b' ? '#475569' : stroke, label: String(n), emoji: stroke === '#1e293b' ? null : cfg.emoji, descripcion: cfg.descripcion, tipo: cfg.tipo };
  }
  return { stroke: '#1e293b', fill: '#0c1420', color: '#475569', label: String(n) };
};

// ── Sub-componente: fichas de jugadores en un casillero ───────────────────────

const FichasCasillero = ({ fichas, fill, miUserId }) => {
  if (!fichas.length) return null;

  const MAX      = 2;
  const visibles = fichas.slice(0, MAX);
  const extra    = fichas.length - MAX;
  const spacing  = 17;
  const totalSlots = visibles.length + (extra > 0 ? 1 : 0);
  const startX   = CELL_W / 2 - ((totalSlots - 1) * spacing) / 2;
  const fy       = CELL_H / 2 + 6;

  return (
    <>
      {visibles.map((f, fi) => {
        const fx       = startX + fi * spacing;
        const esMio    = f.user_id === miUserId;
        const opacidad = miUserId && !esMio ? 0.18 : 1;
        const textColor = ['#f59e0b', '#22c55e', '#00f2ff', '#84cc16', '#ffffff'].includes(f.color) ? '#000' : '#fff';
        const inicial  = (f.nombre_usuario || '?')[0].toUpperCase();
        return (
          <g key={f.user_id} opacity={opacidad}>
            <circle cx={fx + 0.8} cy={fy + 1} r="7" fill="rgba(0,0,0,0.45)" />
            {esMio && miUserId && (
              <circle cx={fx} cy={fy} r="10" fill={f.color} opacity="0.12" className="ficha-halo" />
            )}
            <circle cx={fx} cy={fy} r="7.8" fill="none" stroke={f.color} strokeWidth="0.6" opacity="0.4" />
            <circle cx={fx} cy={fy} r="7"   fill={fill}  stroke={f.color} strokeWidth="1.4" />
            <circle cx={fx} cy={fy} r="5.8" fill={f.color} opacity="0.92" />
            <ellipse cx={fx - 1.5} cy={fy - 2.2} rx="2.8" ry="1.8" fill="white" opacity="0.28" />
            <text x={fx} y={fy + 0.6} textAnchor="middle" dominantBaseline="central"
              fontSize="5.8" fontWeight="800" fill={textColor}
              style={{ userSelect: 'none', fontFamily: 'monospace' }}>
              {inicial}
            </text>
            <title>{f.nombre_usuario}{esMio ? ' (vos)' : ''}</title>
          </g>
        );
      })}

      {extra > 0 && (
        <g opacity={miUserId ? 0.5 : 1}>
          <circle cx={startX + visibles.length * spacing + 0.8} cy={fy + 1} r="7" fill="rgba(0,0,0,0.4)" />
          <circle cx={startX + visibles.length * spacing} cy={fy} r="7.8" fill="none" stroke="#475569" strokeWidth="0.6" opacity="0.4" />
          <circle cx={startX + visibles.length * spacing} cy={fy} r="7"   fill="#0a1020" stroke="#475569" strokeWidth="1.4" />
          <text x={startX + visibles.length * spacing} y={fy + 0.6}
            textAnchor="middle" dominantBaseline="central"
            fontSize="5" fontWeight="bold" fill="#94a3b8" style={{ userSelect: 'none' }}>
            +{extra}
          </text>
          <title>{fichas.slice(MAX).map(f => f.nombre_usuario).join(', ')}</title>
        </g>
      )}
    </>
  );
};

// ── Componente principal ──────────────────────────────────────────────────────

const Tablero = ({
  participantes = [],
  posiciones = {},
  configCasilleros = {},
  miUserId = null,
  // Props para modo movimiento:
  pathCasilleros = null,   // array de casilleros a recorrer (null = modo normal)
  casilleroFinal = null,   // casillero donde el usuario debe hacer click
  onCasilleroClick = null, // callback cuando el usuario clickea el casillero final
}) => {
  // Agrupar fichas por casillero
  const fichasPorCasillero = {};
  participantes.forEach((p, idx) => {
    const pos = (posiciones[p.user_id] || 0) % TOTAL;
    if (!fichasPorCasillero[pos]) fichasPorCasillero[pos] = [];
    fichasPorCasillero[pos].push({ ...p, color: COLORES_FICHAS[idx % COLORES_FICHAS.length] });
  });

  return (
    <div style={{
      background: '#080d14', borderRadius: '10px',
      padding: '10px 14px', border: '1px solid #1e293b',
      width: '100%', boxSizing: 'border-box',
    }}>
      <svg width="100%" viewBox={`0 0 ${TABLE_W} ${TABLE_H}`} style={{ display: 'block' }}>

        {/* Fondos */}
        <rect x="0" y="0" width={TABLE_W} height={TABLE_H} rx="6" fill="#080d14" />
        <rect x={CELL_W} y={CELL_H} width={TABLE_W - CELL_W * 2} height={TABLE_H - CELL_H * 2} rx="4" fill="#0a0f19" />

        <defs>
          <style>{`
            @keyframes pulse-ficha {
              0%, 100% { opacity: 0.15; r: 8; }
              50%       { opacity: 0.35; r: 10; }
            }
            .ficha-halo { animation: pulse-ficha 2s ease-in-out infinite; }
          `}</style>
          {/* Flecha indicadora de dirección */}
          <marker id="arr" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="4" markerHeight="4" orient="auto-start-reverse">
            <path d="M2 1L8 5L2 9" fill="none" stroke="#2a3a4a" strokeWidth="2" strokeLinecap="round" />
          </marker>
        </defs>

        {/* Flecha de dirección */}
        <path d={`M ${CELL_W * 1.6} ${CELL_H / 2} L ${CELL_W * 2.1} ${CELL_H / 2}`}
          stroke="#2a3a4a" strokeWidth="1" fill="none" markerEnd="url(#arr)" />

        {/* Casilleros */}
        {Array.from({ length: TOTAL }, (_, n) => {
          const { col, row } = gridPos(n);
          const x     = col * CELL_W;
          const y     = row * CELL_H;
          const s     = getCasilleroStyle(n, configCasilleros);
          const fichas = fichasPorCasillero[n] || [];
          const tieneEfecto = !!configCasilleros[n] || CASILLEROS_ESPECIALES.has(n);

          // ── Modo movimiento ───────────────────────────────────────────────
          const enPath      = pathCasilleros !== null && pathCasilleros.includes(n);
          const esFinal     = casilleroFinal !== null && n === casilleroFinal;
          const modoPath    = pathCasilleros !== null;
          // Casilleros fuera del path se opacifican
          const opacidadGrupo = modoPath ? (enPath ? 1 : 0.25) : 1;
          const esCLickable   = esFinal && typeof onCasilleroClick === 'function';

          return (
            <g
              key={n}
              transform={`translate(${x}, ${y})`}
              style={{
                opacity: opacidadGrupo,
                transition: 'opacity 0.3s ease',
                cursor: esCLickable ? 'pointer' : 'default',
              }}
              onClick={esCLickable ? onCasilleroClick : undefined}
            >
              {/* Fondo del casillero */}
              <rect x="1" y="1" width={CELL_W - 2} height={CELL_H - 2} rx="4"
                fill={fichas.length > 0 ? '#1a2535' : s.fill}
                stroke={
                  esFinal ? '#00ff88'
                  : fichas.length > 0 ? (s.stroke === '#1e293b' ? '#334155' : s.stroke)
                  : s.stroke
                }
                strokeWidth={esFinal ? '2.5' : fichas.length > 0 ? '1.5' : tieneEfecto ? '1' : '0.6'}
              />

              {/* Glow especial en casillero final */}
              {esFinal && (
                <rect x="1" y="1" width={CELL_W - 2} height={CELL_H - 2} rx="4"
                  fill="#00ff88" opacity="0.10"
                />
              )}

              {/* Glow sutil en casilleros especiales sin fichas */}
              {!esFinal && tieneEfecto && fichas.length === 0 && s.stroke !== '#1e293b' && (
                <rect x="1" y="1" width={CELL_W - 2} height={CELL_H - 2} rx="4" fill={s.stroke} opacity="0.04" />
              )}

              {/* Número de casillero */}
              <text x="4" y="5" textAnchor="start" dominantBaseline="hanging"
                fill={s.color} fontSize="7" fontWeight="600" opacity="0.7">
                {s.label}
              </text>

              {/* Descripción corta del casillero (sin fichas) */}
              {s.descripcion && fichas.length === 0 && s.stroke !== '#1e293b' && (
                <text x={CELL_W / 2} y={CELL_H * 0.65} textAnchor="middle" dominantBaseline="central"
                  fill={s.color} fontSize="5.5" opacity="0.65" style={{ userSelect: 'none' }}>
                  {s.descripcion.length > 20 ? s.descripcion.slice(0, 20) + '…' : s.descripcion}
                </text>
              )}

              {/* Emoji SIEMPRE en esquina superior derecha */}
              {s.emoji && (
                <text x={CELL_W - 5} y="5" textAnchor="end" dominantBaseline="hanging"
                  fontSize="11" style={{ userSelect: 'none' }}>
                  {s.emoji}
                </text>
              )}

              {s.descripcion && <title>{s.descripcion}</title>}

              {/* Indicador de paso en casilleros de path intermedios */}
              {enPath && !esFinal && modoPath && (
                <circle cx={CELL_W / 2} cy={CELL_H / 2} r="4"
                  fill="#00f2ff" opacity="0.5"
                />
              )}

              {/* Indicador "¡AQUÍ!" en casillero final */}
              {esFinal && (
                <text x={CELL_W / 2} y={CELL_H - 4}
                  textAnchor="middle" dominantBaseline="auto"
                  fontSize="6" fontWeight="800" fill="#00ff88" opacity="0.9"
                  style={{ userSelect: 'none', letterSpacing: '0.5px' }}>
                                 </text>
              )}

              {/* Fichas de jugadores */}
              <FichasCasillero fichas={fichas} fill={s.fill} miUserId={miUserId} />
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default Tablero;